import type { CloudCostLine, CloudCostReport } from "@hasheeai/types";
import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "../../api/client";
import { getLocale, t } from "../../i18n";
import { resolveAdminErrorMessage } from "../../lib/admin-error-messages";
export function useCloudCosts(month: string): {
  report: CloudCostReport | null;
  error: string;
  loading: boolean;
  refresh: () => void;
} {
  const [report, setReport] = useState<CloudCostReport | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((r) => r + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setReport(null);
    apiFetch<{ data: CloudCostReport }>(
      `/admin/cloud-costs?month=${month}&refresh=${revision > 0 ? 1 : 0}`,
      {
        signal: controller.signal,
      },
    )
      .then((r) => {
        if (!controller.signal.aborted) setReport(r.data);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setError(
            error instanceof ApiError
              ? `${resolveAdminErrorMessage(error.errorCode, getLocale())} (${error.errorCode})`
              : t("cost.error"),
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [month, revision]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 900000);
    return () => window.clearInterval(timer);
  }, [refresh]);
  return { report, error, loading, refresh };
}
export function exportCosts(lines: CloudCostLine[], month: string): void {
  const columns = [
    "provider",
    "service",
    "resource",
    "start",
    "end",
    "billing_period",
    "quantity",
    "unit",
    "amount",
    "currency",
    "basis",
    "source",
    "reported_amount",
    "cumulative_amount",
    "reconciliation",
  ] as const;
  const escapeCell = (value: unknown): string => {
    const text = String(value ?? "");
    return `"${(/^[=+@-]/.test(text) ? "'" : "") + text.replaceAll('"', '""')}"`;
  };
  const csv = [
    columns.join(","),
    ...lines.map((r) => columns.map((c) => escapeCell(r[c])).join(",")),
  ].join("\r\n");
  const url = URL.createObjectURL(
    new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `cloud-costs-${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export const costMoney = (amount: number): string =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 4,
  }).format(amount);
